import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { MemberReportBucket, MemberReportData, MemberReportQuery } from '@todo/shared';
import {
  MEMBER_REPORT_API_URL, MemberReportError, memberReportApi,
} from '../api/member-report.api';
import { Button } from './ui/Button';
import { Dropdown, type DropdownOption } from './ui/Dropdown';
import { Pagination } from './ui/Pagination';

/**
 * Member Report by Constituency - a read-only Dashboard section.
 *
 * ONE request (GET /constituency) feeds the tiles, both charts and the
 * table. Filters live in the URL query string (from, to, state, district,
 * constituency, bucket) so a view can be shared and survives a reload.
 * The API allows 30 calls a minute, so: refetch only when a filter changes,
 * debounced 400 ms, never on focus or on a timer, and a 429 is shown, not
 * retried. Out-of-order answers are dropped.
 */

const BUCKETS: Array<{ value: MemberReportBucket; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];
const PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#14B8A6', '#F97316', '#0EA5E9', '#A855F7', '#22C55E',
];
const PAGE_SIZE = 25;
const SCROLL_ABOVE = 16;
const POINT_WIDTH = 46;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const COUNT = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (n: number) => COUNT.format(n);

// ------------------------------------------------------------------ dates --

function isoDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: isoDay(from), to: isoDay(to) };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-01" -> "1 Oct"; "2026-10" -> "Oct 2026"; week and year stay as the API sent them. */
function bucketLabel(bucket: string, kind: MemberReportBucket): string {
  if (kind === 'day' && DATE_RE.test(bucket)) {
    return `${Number(bucket.slice(8, 10))} ${MONTHS[Number(bucket.slice(5, 7)) - 1] ?? ''}`;
  }
  if (kind === 'month' && /^\d{4}-\d{2}$/.test(bucket)) {
    return `${MONTHS[Number(bucket.slice(5, 7)) - 1] ?? ''} ${bucket.slice(0, 4)}`;
  }
  return bucket;
}

// ------------------------------------------------------------------ hooks --

function useDebounced<T>(value: T, ms: number): T {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return out;
}

/** The option that matches `value` ignoring case, else `value` itself. */
function canonical(value: string, options: string[]): string {
  if (!value) return '';
  const lower = value.toLowerCase();
  return options.find((o) => o.toLowerCase() === lower) ?? value;
}

function toOptions(all: string, names: string[], current: string): DropdownOption[] {
  const list = current && !names.includes(current) ? [current, ...names] : names;
  return [{ value: '', label: all }, ...list.map((n) => ({ value: n, label: n }))];
}

// ------------------------------------------------------------- primitives --

function Card({
  title, subtitle, right, children, className = '',
}: {
  title: string; subtitle?: string; right?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={`flex flex-col rounded-2xl border border-dash-line bg-dash-raised p-4 shadow-sm md:p-5 ${className}`}>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[17px] font-semibold leading-[22px] text-dash-ink">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[13px] text-dash-muted">{subtitle}</p>}
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}

function Skeleton({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-xl bg-dash-row ${className}`} />;
}

/** Loading / error / empty for ONE card; children render only when there is data. */
function CardState({
  loading, error, empty, emptyText, onRetry, onReset, height, children,
}: {
  loading: boolean; error: string | null; empty: boolean; emptyText: string;
  onRetry: () => void; onReset: () => void; height: string; children: ReactNode;
}) {
  if (error) {
    return (
      <div className={`flex ${height} flex-col items-center justify-center gap-3 text-center`}>
        <p className="max-w-md text-[13px] text-dash-danger">{error}</p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onRetry}>Retry</Button>
          <Button variant="secondary" size="sm" onClick={onReset}>Reset filters</Button>
        </div>
      </div>
    );
  }
  if (loading) return <Skeleton className={height} />;
  if (empty) {
    return (
      <div className={`flex ${height} flex-col items-center justify-center gap-3 text-center`}>
        <p className="text-[13px] text-dash-muted">{emptyText}</p>
        <Button variant="secondary" size="sm" onClick={onReset}>Reset filters</Button>
      </div>
    );
  }
  return <>{children}</>;
}

function Tile({ value, label, loading }: { value?: number; label: string; loading: boolean }) {
  if (loading || value === undefined) return <Skeleton className="h-[74px]" />;
  return (
    <div className="dash-focus flex items-center gap-3 rounded-xl border bg-dash-raised p-4 border-dash-line hover:border-dash-line-strong">
      <div className="min-w-0">
        <p className="text-[26px] font-bold leading-[30px] tracking-tight text-dash-ink tabular-nums">
          {fmt(value)}
        </p>
        <p className="truncate text-[13px] font-medium capitalize text-dash-muted">{label}</p>
      </div>
    </div>
  );
}

const AXIS = { fill: 'var(--dash-muted)', fontSize: 12 };
const TIP = {
  contentStyle: {
    background: 'var(--dash-raised)', border: '1px solid var(--dash-line)',
    borderRadius: 8, color: 'var(--dash-ink)', fontSize: 12,
  },
  labelStyle: { color: 'var(--dash-ink)' },
  itemStyle: { color: 'var(--dash-ink2)' },
} as const;

// ---------------------------------------------------------------- section --

export function MemberReport() {
  const [params, setParams] = useSearchParams();
  const defaults = useMemo(defaultRange, []);

  // ---- filters, straight from the URL
  const rawFrom = params.get('from') ?? '';
  const rawTo = params.get('to') ?? '';
  const from = DATE_RE.test(rawFrom) ? rawFrom : defaults.from;
  const to = DATE_RE.test(rawTo) ? rawTo : defaults.to;
  const bucketParam = params.get('bucket');
  const bucket: MemberReportBucket = BUCKETS.some((b) => b.value === bucketParam)
    ? (bucketParam as MemberReportBucket)
    : 'day';
  const rawState = params.get('state') ?? '';
  const rawDistrict = params.get('district') ?? '';
  const rawConstituency = params.get('constituency') ?? '';

  // ---- dropdown lists (geo, cached per session; report list is the fallback)
  const [states, setStates] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [constituencyNames, setConstituencyNames] = useState<string[]>([]);
  const [geoFailed, setGeoFailed] = useState(false);

  const [data, setData] = useState<MemberReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  /** The Full Breakdown modal (opened from the Members by State card). */
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  /** The donut slice under the pointer (or its legend row); drives the centre text. */
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    if (!breakdownOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBreakdownOpen(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [breakdownOpen]);

  const fallbackStates = useMemo(
    () => [...new Set((data?.constituencies ?? []).map((c) => c.state).filter((s): s is string => !!s && s !== 'Unspecified'))].sort(),
    [data],
  );
  const fallbackConstituencies = useMemo(
    () => (data?.constituencies ?? []).map((c) => c.name).filter((n) => n !== 'Unspecified').sort(),
    [data],
  );
  const stateList = states.length > 0 ? states : fallbackStates;
  const constituencyList = constituencyNames.length > 0 ? constituencyNames : fallbackConstituencies;

  const state = canonical(rawState, stateList);
  const district = canonical(rawDistrict, districts);
  const constituency = canonical(rawConstituency, constituencyList);

  const query: MemberReportQuery = useMemo(
    () => ({ from, to, bucket, state, district, constituency }),
    [from, to, bucket, state, district, constituency],
  );
  const queryKey = useDebounced(JSON.stringify(query), 400);

  useEffect(() => {
    if (!MEMBER_REPORT_API_URL) return;
    memberReportApi.geo('states').then(setStates).catch(() => setGeoFailed(true));
  }, []);

  useEffect(() => {
    if (!MEMBER_REPORT_API_URL || !state) { setDistricts([]); return; }
    let live = true;
    memberReportApi.geo('districts', { state_name: state })
      .then((v) => { if (live) setDistricts(v); })
      .catch(() => { if (live) setDistricts([]); });
    return () => { live = false; };
  }, [state]);

  useEffect(() => {
    if (!MEMBER_REPORT_API_URL) return;
    let live = true;
    memberReportApi.geo('constituencies', { state_name: state, district_name: district })
      .then((v) => { if (live) setConstituencyNames(v); })
      .catch(() => { if (live) setConstituencyNames([]); });
    return () => { live = false; };
  }, [state, district]);

  // ---- the one report request
  useEffect(() => {
    if (!MEMBER_REPORT_API_URL) return;
    const ctrl = new AbortController();
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    memberReportApi
      .report(JSON.parse(queryKey) as MemberReportQuery, ctrl.signal)
      .then((d) => {
        if (id !== reqId.current) return; // an older answer arriving late
        setData(d);
        setLoading(false);
        setPage(1);
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted || id !== reqId.current) return;
        setError(e instanceof MemberReportError ? e.message : 'The report could not be loaded. Try again.');
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [queryKey, tick]);

  // ---- URL updates
  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
  }
  const reset = () => setParams(new URLSearchParams(), { replace: true });

  function setDate(key: 'from' | 'to', value: string) {
    if (!DATE_RE.test(value)) return;
    // Keep the range valid: moving one end past the other drags it along.
    if (key === 'from') update({ from: value, ...(value > to ? { to: value } : {}) });
    else update({ to: value, ...(value < from ? { from: value } : {}) });
  }

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await memberReportApi.downloadPdf(query);
    } catch (e) {
      setDownloadError(e instanceof MemberReportError ? e.message : 'The PDF could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  }

  // ---- not configured
  if (!MEMBER_REPORT_API_URL) {
    return (
      <section className="mb-4 rounded-2xl border border-dash-danger/40 bg-dash-raised p-5">
        <h3 className="text-[17px] font-semibold text-dash-ink">Member Report by Constituency</h3>
        <p className="mt-1 text-[13px] text-dash-danger">
          The member report is not configured. Set VITE_MEMBER_REPORT_API_URL in the web .env and rebuild.
        </p>
      </section>
    );
  }

  const blocked = loading || error !== null || data === null;
  const summary = data?.summary;
  const showLoading = loading && data === null;
  const trendHasData = (data?.trend ?? []).some((t) => t.count > 0);
  const donut = (data?.byState ?? []).filter((s) => s.count > 0);
  const top15 = (data?.constituencies ?? []).slice(0, 15);
  const rows = data?.constituencies ?? [];
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const cardProps = { loading: showLoading, error, onRetry: () => setTick((t) => t + 1), onReset: reset };

  const chips: Array<{ label: string; clear: Record<string, string> }> = [];
  if (state) chips.push({ label: `State: ${state}`, clear: { state: '', district: '', constituency: '' } });
  if (district) chips.push({ label: `District: ${district}`, clear: { district: '', constituency: '' } });
  if (constituency) chips.push({ label: `Constituency: ${constituency}`, clear: { constituency: '' } });

  const trendPoints = data?.trend.length ?? 0;
  const scrollTrend = trendPoints > SCROLL_ABOVE;
  const trendChart = (width?: number) => (
    <AreaChart
      data={data?.trend ?? []}
      width={width}
      height={260}
      margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
    >
      <defs>
        <linearGradient id="memberTrend" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke="var(--dash-line)" vertical={false} />
      <XAxis dataKey="bucket" tick={AXIS} tickLine={false} axisLine={false}
        tickFormatter={(v: string) => bucketLabel(v, data?.range.bucket ?? bucket)} interval={0} />
      <YAxis tick={AXIS} tickLine={false} axisLine={false} width={40} allowDecimals={false}
        tickFormatter={(v: number) => fmt(v)} />
      <Tooltip {...TIP} formatter={(v) => [fmt(Number(v)), 'Members']}
        labelFormatter={(v) => bucketLabel(String(v), data?.range.bucket ?? bucket)} />
      <Area type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} fill="url(#memberTrend)"
        isAnimationActive={false} />
    </AreaChart>
  );

  return (
    <section className={`mb-4 space-y-4 ${loading && data ? 'opacity-80' : ''}`} aria-busy={loading}>
      <div>
        <h2 className="text-[17px] font-semibold leading-[22px] text-dash-ink">Member Report by Constituency</h2>
      </div>

      {/* Toolbar + filters */}
      <div className="rounded-2xl border border-dash-line bg-dash-raised p-4 shadow-sm md:p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-[12px] font-medium text-dash-muted">
              From
              <input type="date" value={from} max={to} onChange={(e) => setDate('from', e.target.value)}
                className="dash-focus mt-1 block h-10 rounded-lg border border-dash-line bg-dash-field px-3 text-[14px] text-dash-ink" />
            </label>
            <label className="text-[12px] font-medium text-dash-muted">
              To
              <input type="date" value={to} min={from} onChange={(e) => setDate('to', e.target.value)}
                className="dash-focus mt-1 block h-10 rounded-lg border border-dash-line bg-dash-field px-3 text-[14px] text-dash-ink" />
            </label>
          </div>
          <Button variant="primary" size="md" onClick={download} disabled={blocked || downloading}>
            {downloading ? 'Preparing PDF...' : 'Download PDF'}
          </Button>
        </div>
        {downloadError && <p className="mt-2 text-[13px] text-dash-danger" role="alert">{downloadError}</p>}

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Dropdown
            className="min-w-[12rem] flex-1"
            placeholder="All States"
            value={state}
            options={toOptions('All States', stateList, state)}
            onChange={(v) => update({ state: v, district: '', constituency: '' })}
            hint={geoFailed ? 'Using the report’s own list' : undefined}
          />
          <Dropdown
            className="min-w-[12rem] flex-1"
            placeholder="All Districts"
            value={district}
            disabled={!state}
            options={toOptions('All Districts', districts, district)}
            onChange={(v) => update({ district: v, constituency: '' })}
          />
          <Dropdown
            className="min-w-[12rem] flex-1"
            placeholder="All Constituencies"
            value={constituency}
            options={toOptions('All Constituencies', constituencyList, constituency)}
            onChange={(v) => update({ constituency: v })}
          />
          <button type="button" onClick={reset}
            className="dash-focus h-10 rounded text-[13px] font-medium text-dash-action-text hover:underline">
            Reset
          </button>
        </div>

        {chips.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {chips.map((c) => (
              <li key={c.label}
                className="inline-flex items-center gap-1.5 rounded-full border border-dash-line bg-dash-row py-1 pl-3 pr-1.5 text-[12px] text-dash-ink2">
                {c.label}
                <button type="button" aria-label={`Remove ${c.label}`} onClick={() => update(c.clear)}
                  className="dash-focus grid h-5 w-5 place-items-center rounded-full text-dash-muted hover:bg-dash-line hover:text-dash-ink">
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* KPI tiles */}
      {error ? null : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 2xl:grid-cols-6">
          <Tile loading={showLoading} value={summary?.total} label="Total members" />
          <Tile loading={showLoading} value={summary?.today} label="Today" />
          <Tile loading={showLoading} value={summary?.thisWeek} label="This week" />
          <Tile loading={showLoading} value={summary?.thisMonth} label="This month" />
          <Tile loading={showLoading} value={summary?.constituencies} label="Constituencies" />
          <Tile loading={showLoading} value={summary?.states} label="States" />
        </div>
      )}

      {/* Trend */}
      <Card
        title="Registration Trend"
        subtitle="New member registrations across the selected range"
        right={
          <div role="group" aria-label="Trend granularity" className="inline-flex rounded-lg border border-dash-line bg-dash-row p-0.5">
            {BUCKETS.map((b) => (
              <button key={b.value} type="button" aria-pressed={bucket === b.value}
                onClick={() => update({ bucket: b.value === 'day' ? '' : b.value })}
                className={`dash-focus rounded-md px-3 py-1 text-[13px] font-medium ${bucket === b.value
                  ? 'bg-dash-raised text-dash-ink shadow-sm' : 'text-dash-muted hover:text-dash-ink'}`}>
                {b.label}
              </button>
            ))}
          </div>
        }
      >
        <CardState {...cardProps} empty={!trendHasData} emptyText="No registrations in this range." height="h-[260px]">
          {scrollTrend ? (
            <div className="overflow-x-auto">{trendChart(trendPoints * POINT_WIDTH)}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>{trendChart()}</ResponsiveContainer>
          )}
        </CardState>
      </Card>

      {/* Top 15 + states */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="Top 15 Constituencies" subtitle="Constituencies with the most members">
          <CardState {...cardProps} empty={top15.length === 0} emptyText="No constituencies match these filters." height="h-[300px]">
            <div>
              <ResponsiveContainer width="100%" height={top15.length * 28 + 16}>
                <BarChart data={top15} layout="vertical" margin={{ top: 0, right: 56, bottom: 0, left: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={150} tick={AXIS} tickLine={false} axisLine={false}
                    interval={0} tickFormatter={(v: string) => (v.length > 22 ? `${v.slice(0, 21)}…` : v)} />
                  <Tooltip {...TIP} cursor={{ fill: 'var(--dash-row)' }} formatter={(v) => [fmt(Number(v)), 'Members']} />
                  <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} barSize={16} isAnimationActive={false}>
                    <LabelList dataKey="count" position="right" formatter={(v) => fmt(Number(v))}
                      style={{ fill: 'var(--dash-ink2)', fontSize: 12 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardState>
        </Card>

        <Card title="Members by State" subtitle="Share of members in the top states"
          right={
            <Button variant="secondary" size="sm" disabled={loading || rows.length === 0}
              onClick={() => { setPage(1); setBreakdownOpen(true); }}>
              Full Breakdown{rows.length > 0 ? ` (${fmt(rows.length)})` : ''}
            </Button>
          }>
          <CardState {...cardProps} empty={donut.length === 0} emptyText="No members match these filters." height="h-[300px]">
            {/* Centred in the card so it never leaves a hole beside the taller
                Top 15 chart. The hover readout REPLACES the centre total - a
                floating tooltip would be drawn on top of it. */}
            <div className="flex flex-1 flex-wrap items-center justify-center gap-8 py-2">
              <div className="relative h-[260px] w-[260px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donut} dataKey="count" nameKey="name" innerRadius="64%" outerRadius="94%"
                      paddingAngle={2} stroke="none" isAnimationActive={false}
                      onMouseEnter={(_: unknown, i: number) => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(null)}>
                      {donut.map((s, i) => (
                        <Cell key={s.name} fill={PALETTE[i % PALETTE.length]}
                          opacity={hoverIdx === null || hoverIdx === i ? 1 : 0.35} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
                  {hoverIdx !== null && donut[hoverIdx] ? (
                    <>
                      <span className="max-w-full truncate text-[12px] font-semibold text-dash-ink2">
                        {donut[hoverIdx].name}
                      </span>
                      <span className="text-[26px] font-bold leading-[30px] text-dash-ink tabular-nums">
                        {fmt(donut[hoverIdx].count)}
                      </span>
                      <span className="text-[11px] font-semibold tracking-wider text-dash-muted">
                        {summary && summary.total > 0
                          ? `${PCT.format((donut[hoverIdx].count / summary.total) * 100)}%`
                          : 'MEMBERS'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-[26px] font-bold leading-[30px] text-dash-ink tabular-nums">
                        {fmt(summary?.total ?? 0)}
                      </span>
                      <span className="text-[11px] font-semibold tracking-wider text-dash-muted">MEMBERS</span>
                    </>
                  )}
                </div>
              </div>
              <ul className="min-w-[12rem] max-w-[22rem] flex-1 space-y-1">
                {donut.map((s, i) => (
                  <li key={s.name}
                    onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}
                    className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition-colors ${hoverIdx === i ? 'bg-dash-row' : ''}`}>
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="truncate text-dash-ink">{s.name}</span>
                    <span className="ml-auto tabular-nums text-dash-muted">{fmt(s.count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardState>
        </Card>
      </div>

      {/* Full breakdown - a modal, opened from the Members by State card */}
      {breakdownOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-label="Full Breakdown">
          <div className="absolute inset-0 bg-black/50" onClick={() => setBreakdownOpen(false)} />
          <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-dash-line bg-dash-raised shadow-xl">
            <header className="flex items-start justify-between gap-3 border-b border-dash-line p-4 md:p-5">
              <div>
                <h3 className="text-[17px] font-semibold leading-[22px] text-dash-ink">Full Breakdown</h3>
                <p className="mt-0.5 text-[13px] text-dash-muted">
                  All constituencies with rank, member count, and share
                </p>
              </div>
              <button type="button" autoFocus onClick={() => setBreakdownOpen(false)} aria-label="Close"
                className="dash-focus rounded-lg px-2 py-1 text-[18px] leading-none text-dash-muted hover:bg-dash-row hover:text-dash-ink">
                &times;
              </button>
            </header>
            <div className="min-h-0 flex-1 overflow-auto px-4 md:px-5">
              <table className="w-full min-w-[34rem] text-left text-[13px]">
                <thead className="sticky top-0 bg-dash-raised">
                  <tr className="border-b border-dash-line text-dash-muted">
                    <th className="py-2 pr-3 font-medium">Rank</th>
                    <th className="py-2 pr-3 font-medium">Constituency</th>
                    <th className="py-2 pr-3 font-medium">State</th>
                    <th className="py-2 pr-3 text-right font-medium">Members</th>
                    <th className="py-2 text-right font-medium">Share %</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => (
                    <tr key={`${r.name}-${r.state ?? ''}-${i}`} className="border-b border-dash-line/60 last:border-0">
                      <td className="py-2 pr-3 tabular-nums text-dash-muted">#{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td className="py-2 pr-3 text-dash-ink">{r.name}</td>
                      <td className="py-2 pr-3 text-dash-ink2">{r.state ?? '—'}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-dash-ink">{fmt(r.count)}</td>
                      <td className="py-2 text-right tabular-nums text-dash-ink2">{PCT.format(r.percentage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-dash-line p-4 md:px-5">
              <Pagination page={page} pageSize={PAGE_SIZE} total={rows.length} onPageChange={setPage}
                noun="constituencies" />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}
